// =============================================================================
// API client — wraps fetch with Amplify auth token injection
//
// ⚠️  TWO-IDENTITY MODEL — read before touching auth logic here.
//
// apiFetch ALWAYS attaches the REAL signed-in user's Cognito JWT. When admin
// preview mode is active, the frontend also sends a preview header so the
// backend can resolve an effective actor for authorization and ownership.
//
// This is intentional:
//   • The Cognito session belongs to the real signed-in admin.
//   • Client-side code cannot forge a different user's JWT.
//   • All API calls are therefore made with real admin credentials.
//
// For endpoints that need to simulate another user's server-side visibility
// rules (e.g. "what would user X see in the library?"), pass the query param:
//
//   apiFetch(`/library?previewAs=${viewAsUser.userId}`)
//
// The backend honours ?previewAs only for privileged callers (admin/mod) and
// re-applies the target user's DB-backed access rules under the admin's creds.
//
// Full two-identity model documented in:
//   src/store/slices/authSlice.ts  (module-level comment)
// =============================================================================
import { fetchAuthSession } from 'aws-amplify/auth';

const API_BASE = import.meta.env.VITE_API_BASE_URL;

interface PreviewContext {
  userId?: string;
  role?: string;
}

let previewContext: PreviewContext = {};

export function setApiPreviewContext(next: PreviewContext) {
  previewContext = next;
}

async function getToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString();
  } catch {
    return undefined;
  }
}

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const token = await getToken();

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(previewContext.userId ? { 'X-Darkfeld-Preview-As-User': previewContext.userId } : {}),
    ...(previewContext.role ? { 'X-Darkfeld-Preview-As-Role': previewContext.role } : {}),
    ...(options.headers as Record<string, string> | undefined ?? {}),
  };

  const res = await fetch(`${API_BASE}${path}`, { ...options, headers });

  if (res.status === 204) return undefined as T;

  const data = await res.json().catch(() => null);

  if (!res.ok) {
    throw new ApiError(
      res.status,
      (data as any)?.error ?? 'Unknown',
      (data as any)?.message ?? res.statusText,
    );
  }

  return data as T;
}
